import type { RouteAttr, TripHistory } from "@/types/fleet";

export interface RouteAttrColumnDef {
  id: number;
  title: string;
  key: string;
  /** Unit from the input definition, for exports that put the unit in the header. */
  unit?: string;
}

/** True when a dynamic report column title represents tank fuel level. */
export function isFuelLevelAttrTitle(title: string): boolean {
  const normalized = title.trim().toLowerCase();
  return (
    normalized.includes("nivo goriva") ||
    normalized.includes("razina goriva") ||
    normalized.includes("fuel level") ||
    (normalized.includes("fuel") && normalized.includes("level"))
  );
}

/** True when a dynamic report column title represents engine RPM. */
export function isEngineRpmAttrTitle(title: string): boolean {
  const normalized = title.trim().toLowerCase();
  return (
    normalized === "rpm" ||
    normalized.includes("obrtaj") ||
    normalized.includes("engine rpm") ||
    normalized.includes("engine speed")
  );
}

export interface MergedRouteAttrColumnDef extends RouteAttrColumnDef {
  /** Every input id that resolved to this title, lowest first. */
  ids: number[];
}

/**
 * Collapse columns whose input definitions resolved to the same title.
 *
 * The input map is keyed `${device_type_id}:${inputId}`, so one logical sensor carries a
 * different input id on each device type. Across a mixed fleet that surfaced as two columns both
 * titled "Fuel level", each populated only for its own device type and empty for the rest.
 *
 * The representative id (and therefore the column key) is the lowest, so column order is stable.
 */
export function mergeAttrColumnsByTitle(
  columns: readonly RouteAttrColumnDef[],
): MergedRouteAttrColumnDef[] {
  const byTitle = new Map<string, MergedRouteAttrColumnDef>();
  for (const column of columns) {
    const titleKey = column.title.trim().toLowerCase();
    const existing = byTitle.get(titleKey);
    if (existing) {
      existing.ids.push(column.id);
      // A later definition can carry the unit an earlier one lacked.
      if (!existing.unit && column.unit) existing.unit = column.unit;
      continue;
    }
    byTitle.set(titleKey, { ...column, ids: [column.id] });
  }
  return Array.from(byTitle.values());
}

/**
 * Union of attr columns across records, sorted by input id.
 *
 * Typed on `attrs` alone rather than on `TripHistory`: live vehicles carry the same enriched
 * shape, so Live Tracking's fleet table builds its sensor columns through this too.
 */
export function collectRouteAttrColumns(
  records: readonly { attrs?: RouteAttr[] }[],
): RouteAttrColumnDef[] {
  const byId = new Map<number, { title: string; unit?: string }>();
  for (const trip of records) {
    if (!Array.isArray(trip.attrs)) continue;
    for (const attr of trip.attrs) {
      if (!attr?.displayTitle?.trim()) continue;
      byId.set(attr.id, { title: attr.displayTitle, unit: resolveAttrUnit(attr) });
    }
  }
  return Array.from(byId.entries())
    .sort(([a], [b]) => a - b)
    .map(([id, { title, unit }]) => ({
      id,
      title,
      key: `attr_${id}`,
      unit,
    }));
}

/** Flatten attr `realValue`s onto row keys for numeric exports — the twin of `flattenRouteAttrs`. */
export function rawRouteAttrValues(trip: Pick<TripHistory, "attrs">): Record<string, number> {
  const raw: Record<string, number> = {};
  if (Array.isArray(trip.attrs)) {
    for (const attr of trip.attrs) {
      // Non-finite values fall through so the exporter uses the display string instead.
      if (Number.isFinite(attr.realValue)) raw[`attr_${attr.id}`] = attr.realValue;
    }
  }
  return raw;
}

export function getRouteAttrDisplayValue(trip: TripHistory, attrId: number): string {
  const attr = trip.attrs?.find((a) => a.id === attrId);
  return attr?.displayValue ?? "-";
}

export interface RouteAttrSummary {
  id: number;
  title: string;
  total: number;
  displayTotal: string;
  unit?: string;
}

function parseDisplayUnit(displayValue: string): string {
  const match = displayValue.trim().match(/^[\d.,]+\s+(.+)$/);
  return match?.[1]?.trim() ?? "";
}

/**
 * `unit` comes straight from the input definition. The regex fallback covers attrs enriched
 * before `unit` was carried through, which are otherwise indistinguishable.
 */
function resolveAttrUnit(attr: RouteAttr): string | undefined {
  return attr.unit?.trim() || parseDisplayUnit(attr.displayValue) || undefined;
}

function displayUsesDecimals(displayValue: string): boolean {
  const match = displayValue.trim().match(/^([\d.,]+)/);
  if (!match) return false;
  return match[1].includes(".") || match[1].includes(",");
}

function formatSummedTotal(total: number, unit: string, useDecimals: boolean): string {
  const rounded = useDecimals ? Math.round(total * 100) / 100 : Math.round(total);
  const formatted = useDecimals ? rounded.toFixed(2) : String(rounded);
  return unit ? `${formatted} ${unit}` : formatted;
}

/** Sum enriched per-route `realValue` per report attr across trips (for report totals). */
export function summarizeRouteAttrs(trips: TripHistory[]): RouteAttrSummary[] {
  return collectRouteAttrColumns(trips).map(({ id, title }) => {
    let total = 0;
    let sampleDisplay = "";
    let sampleUnit = "";

    for (const trip of trips) {
      const attr = trip.attrs?.find((a) => a.id === id);
      if (!attr || !Number.isFinite(attr.realValue)) continue;
      total += attr.realValue;
      if (!sampleDisplay && attr.displayValue) {
        sampleDisplay = attr.displayValue;
      }
      if (!sampleUnit && attr.unit?.trim()) {
        sampleUnit = attr.unit.trim();
      }
    }

    const unit = sampleUnit || parseDisplayUnit(sampleDisplay);
    return {
      id,
      title,
      total,
      displayTotal: formatSummedTotal(total, unit, displayUsesDecimals(sampleDisplay)),
      unit: unit || undefined,
    };
  });
}

/** Flatten attrs onto row object keys for table sort/export. */
export function flattenRouteAttrs<T extends TripHistory>(
  trip: T,
): T & Record<string, string> {
  const flat: Record<string, string> = {};
  if (Array.isArray(trip.attrs)) {
    for (const attr of trip.attrs) {
      flat[`attr_${attr.id}`] = attr.displayValue;
    }
  }
  return { ...trip, ...flat };
}
